import type { VerificationRequest } from 'matrix-js-sdk/src/crypto-api';
import { VerificationPhase } from 'matrix-js-sdk/src/crypto-api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  isLoginSasVerificationPending,
  isLoginSecretStorageUnlockActive,
} from 'contexts/MatrixContext/loginSecretStorageUnlock';
import { CrossSigningStatusEnum } from 'contexts/MatrixDeviceContext/types';

type UseSasVerificationModalsParams = {
  crossSigningStatus: CrossSigningStatusEnum;
  hasCompletedSelfVerification: boolean;
  activeRequest: VerificationRequest | null;
  hasChosenSasVerification: boolean;
};

const INACTIVE_PHASES = [VerificationPhase.Done, VerificationPhase.Cancelled];

const useSasVerificationModals = ({
  crossSigningStatus,
  hasCompletedSelfVerification,
  activeRequest,
  hasChosenSasVerification,
}: UseSasVerificationModalsParams) => {
  const [shouldOpenIncomingModal, setShouldOpenIncomingModal] = useState(false);
  const [waitingModalDismissed, setWaitingModalDismissed] = useState(false);
  const lastRequestIdRef = useRef<string | null>(null);

  const shouldShowWaitingModal = useMemo(() => {
    const isWaitingForVerification = hasChosenSasVerification && !waitingModalDismissed;
    if (!isWaitingForVerification || hasCompletedSelfVerification) return false;

    // During login unlock the cross-signing status stays UNKNOWN (context client is null).
    // The gate only opens when the device is unverified, so treat UNKNOWN as NOT_VERIFIED here.
    // `isLoginSasVerificationPending()` keeps this true across the pre_sync → post_bootstrap gate
    // transition, when `isLoginSecretStorageUnlockActive()` is momentarily false but the SAS flow is
    // still in progress (BUG-006) — otherwise the waiting modal would close and strand the user.
    const isLoginUnlock = isLoginSecretStorageUnlockActive() || isLoginSasVerificationPending();
    const isUnverifiedDevice =
      crossSigningStatus === CrossSigningStatusEnum.NOT_VERIFIED ||
      (isLoginUnlock && crossSigningStatus === CrossSigningStatusEnum.UNKNOWN);

    return isUnverifiedDevice;
  }, [crossSigningStatus, hasCompletedSelfVerification, hasChosenSasVerification, waitingModalDismissed]);

  const shouldShowIncomingModal = useMemo(() => {
    const hasActiveIncomingRequest =
      activeRequest && !activeRequest.initiatedByMe && !INACTIVE_PHASES.includes(activeRequest.phase);
    const isStatusKnown = crossSigningStatus !== CrossSigningStatusEnum.UNKNOWN;

    return shouldOpenIncomingModal && isStatusKnown && hasActiveIncomingRequest;
  }, [shouldOpenIncomingModal, crossSigningStatus, activeRequest]);

  const isRequestReady = useMemo(
    () => Boolean(activeRequest && [VerificationPhase.Ready, VerificationPhase.Started].includes(activeRequest.phase)),
    [activeRequest]
  );

  const openIncomingModal = useCallback(() => {
    if (activeRequest?.transactionId) {
      lastRequestIdRef.current = activeRequest.transactionId;
    }
    setShouldOpenIncomingModal(true);
  }, [activeRequest]);

  const dismissWaitingModal = useCallback(() => {
    setWaitingModalDismissed(true);
  }, []);

  useEffect(() => {
    if (!activeRequest || activeRequest.initiatedByMe || INACTIVE_PHASES.includes(activeRequest.phase)) {
      setShouldOpenIncomingModal(false);
      lastRequestIdRef.current = null;
      return;
    }

    const currentTransactionId = activeRequest.transactionId || null;
    const lastRequestId = lastRequestIdRef.current;

    if (lastRequestId && currentTransactionId !== lastRequestId) {
      setShouldOpenIncomingModal(false);
    }

    lastRequestIdRef.current = currentTransactionId;
  }, [activeRequest]);

  useEffect(() => {
    if (
      activeRequest &&
      activeRequest.initiatedByMe &&
      activeRequest.phase === VerificationPhase.Cancelled &&
      !waitingModalDismissed
    ) {
      setWaitingModalDismissed(true);
    }
  }, [activeRequest, waitingModalDismissed]);

  useEffect(() => {
    if (crossSigningStatus === CrossSigningStatusEnum.VERIFIED) {
      setWaitingModalDismissed(false);
    }
  }, [crossSigningStatus]);

  useEffect(() => {
    if (hasChosenSasVerification && activeRequest && !INACTIVE_PHASES.includes(activeRequest.phase)) {
      setWaitingModalDismissed(false);
    }
  }, [hasChosenSasVerification, activeRequest]);

  return { shouldShowWaitingModal, shouldShowIncomingModal, isRequestReady, openIncomingModal, dismissWaitingModal };
};

export default useSasVerificationModals;
